import EmptyImage from "@/assets/images/emptyActionItem.svg";
import ConfirmPopoverButton from "@/components/shared/DeleteWithConfirmButton";
import IconGenerator from "@/components/shared/IconGenerator";
import {
  useRecapDeleteDecision,
  useRecapUndoDeleteDecision,
} from "@/features/hooks/useRecap";
import useResponsive from "@/hooks/useResponsive.hook";
import { DecisionItem } from "@/interfaces/IRecap";
import { useAppActions } from "@/store/selectors/AppSelectors";
import { DeleteConfirmButton, Empty, Icon } from "@nexus/shared-ui";
import {
  Button,
  Divider,
  Drawer,
  Flex,
  Image,
  List,
  Table,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const { Text, Paragraph } = Typography;

const Decisions = ({
  decisions = [],
  sessionId,
  onEdit,
  showDeleted = false,
}: {
  decisions?: DecisionItem[];
  sessionId: string;
  onEdit?: (item: DecisionItem) => void;
  showDeleted?: boolean;
}) => {
  const { t } = useTranslation();
  const isResponsive = useResponsive();
  const { setMessage } = useAppActions();

  const [isDecisionDrawerOpen, setIsDecisionDrawerOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState<DecisionItem | null>(
    null
  );

  const { mutate: deleteDecision, isPending: isDeletingDecision } =
    useRecapDeleteDecision();

  const { mutate: undoDeleteDecision, isPending: isRestoringDecision } =
    useRecapUndoDeleteDecision();

  const onOpenDecisionDrawer = (record: DecisionItem) => {
    setSelectedDecision(record);
    setIsDecisionDrawerOpen(true);
  };

  const onCloseDecisionDrawer = () => {
    setSelectedDecision(null);
    setIsDecisionDrawerOpen(false);
  };

  const onCopy = async (text?: string | null) => {
    if (!text) {
      setMessage({
        type: "error",
        text: t("recap.failedCopy"),
      });

      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setMessage({
        type: "success",
        text: t("recap.successfulCopy"),
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: t("recap.failedCopy"),
      });
    }
  };

  const onCopyClicked = async () => {
    await onCopy(selectedDecision?.description);
    onCloseDecisionDrawer();
  };

  const onEditClicked = () => {
    if (selectedDecision) {
      onEdit?.(selectedDecision);
    }

    onCloseDecisionDrawer();
  };

  const onDelete = (record?: DecisionItem | null) => {
    if (!record?.id) return;

    deleteDecision(
      {
        sessionId,
        id: record?.id,
      },
      {
        onSuccess: () => {
          onCloseDecisionDrawer();
        },
      }
    );
  };

  const onRestore = (record?: DecisionItem | null) => {
    if (!record?.id) return;

    undoDeleteDecision(
      {
        sessionId,
        id: record?.id,
      },
      {
        onSuccess: () => {
          onCloseDecisionDrawer();
        },
      }
    );
  };

  const tableTitle = useMemo(
    () => (title: string) => <Text className="font-weight-500">{title}</Text>,
    []
  );

  if (!decisions?.length) {
    return (
      <Flex justify="center">
        <Empty
          title={t(
            showDeleted ? "recap.emptyDeletedDecisions" : "recap.noData"
          )}
          icon={<Image src={EmptyImage} preview={false} />}
        />
      </Flex>
    );
  }

  const columns: ColumnsType<DecisionItem> = [
    {
      title: "#",
      key: "rowIndex",
      width: 40,
      align: "center",
      render: (_: string, _record: DecisionItem, index: number) => (
        <Text>{index + 1}</Text>
      ),
    },
    {
      title: tableTitle(t("recap.title")),
      dataIndex: "title",
      key: "title",
      width: 800,
      render: (value: string, _record, index) => {
        return (
          <Flex align="center" gap={20}>
            <Flex align="center" gap={8}>
              {_record?.createdByRosa && (
                <IconGenerator name={"RosaLogo"} size={16} />
              )}
              <Paragraph
                ellipsis={{
                  rows: 2,
                  tooltip: value,
                }}
                className="h7 m-0"
              >
                {value}
                {_record?.updateDate && _record?.updateDate !== null && (
                  <span>
                    {""}
                    <Text className="h8 mx-2" type="secondary">
                      ( {t("recap.edited")})
                    </Text>
                  </span>
                )}
              </Paragraph>
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: tableTitle(t("general.action")),
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Flex justify="center" align="center" gap={8}>
          <Tooltip title={t("actions.copy")}>
            <Button
              type="text"
              size="small"
              icon={<Icon name={"UilCopyAlt"} size={16} color="primary" />}
              onClick={() => onCopy(record?.title)}
            />
          </Tooltip>

          {showDeleted ? (
            <Tooltip title={t("recap.cannotEditDeleted")}>
              <Button
                type="text"
                size="small"
                icon={
                  <Icon
                    name="UilEditAlt"
                    size={16}
                    color="var(--color-disabled)"
                  />
                }
                disabled
              />
            </Tooltip>
          ) : (
            <Tooltip title={t("actions.edit")}>
              <Button
                type="text"
                size="small"
                icon={<Icon name={"UilEditAlt"} size={16} color="primary" />}
                onClick={() => onEdit?.(record)}
              />
            </Tooltip>
          )}

          {showDeleted ? (
            <ConfirmPopoverButton
              item={record}
              onConfirm={onRestore}
              disabled={isRestoringDecision}
              iconName="UilRedo"
              variant="primary"
              confirmTextKey="recap.restoreConfirm"
              buttonTextKey="actions.restore"
              tooltipKey="actions.restore"
            />
          ) : (
            <Tooltip title={t("actions.delete")}>
              <DeleteConfirmButton
                item={record}
                onDeleteConfirm={onDelete}
                disabled={isDeletingDecision}
                withTitle={false}
              />
            </Tooltip>
          )}
        </Flex>
      ),
    },
  ];

  if (isResponsive) {
    return (
      <>
        <div>
          <List
            className="recap-decisions"
            dataSource={decisions}
            split={false}
            renderItem={(item: DecisionItem, index) => (
              <List.Item key={item?.id}>
                <Flex align="stretch" gap={12} className="w-100">
                  <Flex align="center" className="recap-decisions__index">
                    <Text>{index + 1}</Text>
                  </Flex>

                  <div className="recap-decisions__line" />

                  <Flex flex={1} className="min-w-0">
                    <Flex
                      align="flex-start"
                      justify="space-between"
                      gap={8}
                      className="w-100"
                    >
                      <Text className="h7">
                        {item?.createdByRosa && (
                          <span className="rosa-icon">
                            <IconGenerator name={"RosaLogo"} size={16} />
                          </span>
                        )}
                        {item?.title}
                      </Text>

                      <Button
                        type="text"
                        size="small"
                        icon={
                          <Icon
                            name="UilEllipsisV"
                            size={16}
                            color="textColor"
                          />
                        }
                        onClick={() => onOpenDecisionDrawer(item)}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </List.Item>
            )}
          />
        </div>

        <Drawer
          open={isDecisionDrawerOpen}
          placement="bottom"
          onClose={onCloseDecisionDrawer}
          closable={false}
          height="auto"
          rootClassName="recap-decisions__drawer"
        >
          <Flex vertical align="flex-start">
            <Button
              className="p-0"
              type="text"
              icon={<Icon name="UilCopyAlt" size={24} color="primary" />}
              onClick={onCopyClicked}
            >
              {t("actions.copy")}
            </Button>

            <Divider className="my-2" />

            {!showDeleted && (
              <>
                <Button
                  type="text"
                  className="p-0"
                  icon={<Icon name="UilEditAlt" size={24} color="primary" />}
                  onClick={onEditClicked}
                >
                  {t("actions.edit")}
                </Button>

                <Divider className="my-2" />

                <ConfirmPopoverButton
                  className="p-0"
                  item={selectedDecision}
                  onConfirm={onDelete}
                  disabled={isDeletingDecision}
                  withTitle
                  iconSize={24}
                  iconName="UilTrashAlt"
                  iconColor="error"
                />
              </>
            )}

            {showDeleted && (
              <ConfirmPopoverButton
                className="p-0"
                item={selectedDecision}
                onConfirm={onRestore}
                disabled={isRestoringDecision}
                withTitle
                iconSize={24}
                iconName="UilRedo"
                iconColor="primary"
                variant="primary"
                confirmTextKey="recap.restoreConfirm"
                buttonTextKey="actions.restore"
              />
            )}
          </Flex>
        </Drawer>
      </>
    );
  }

  return (
    <div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={decisions}
        pagination={false}
        scroll={{ x: 900, y: 500 }}
        tableLayout="fixed"
      />
    </div>
  );
};

export default Decisions;
